import React from 'react';

// A deliberately tiny Markdown subset for event descriptions (SAM-305).
//
// Scope, on purpose: paragraphs, headings (# to ###), bold, italic, bullet/numbered lists, links.
// Nothing else. This is NOT a general Markdown parser — it never touches
// dangerouslySetInnerHTML and never parses raw HTML tags out of the input.
// Text content is always emitted as React children, so React escapes it the
// same way it would escape any other string — a description containing
// `<script>` renders as the literal text "<script>", it can't execute.
//
// Storage format is plain Markdown text. A pre-existing plain-text
// description (no Markdown syntax) round-trips unchanged: it just becomes a
// single paragraph, so nothing needs migrating.

const ALLOWED_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:'];

const isSafeUrl = (url: string): boolean => {
  try {
    // Relative/invalid URLs throw here; resolve against a dummy base so a
    // plain "example.com" (no protocol) is treated as unsafe rather than
    // silently inheriting the current page's protocol.
    const parsed = new URL(url);
    return ALLOWED_LINK_PROTOCOLS.includes(parsed.protocol);
  } catch {
    return false;
  }
};

// Inline formatting: **bold**, *italic*/_italic_, [text](url).
const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  // Bold must be tried before italic so **x** isn't read as *·*x*·*.
  const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)|(?:\*|_)([^*_]+?)(?:\*|_)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const key = `${keyPrefix}-${i++}`;
    if (match[1] !== undefined) {
      nodes.push(<strong key={key}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      const label = match[2];
      const url = match[3];
      if (isSafeUrl(url)) {
        nodes.push(
          <a key={key} href={url} target="_blank" rel="noopener noreferrer nofollow">
            {label}
          </a>,
        );
      } else {
        // Unsafe scheme (e.g. javascript:) — render as plain text, not a link.
        nodes.push(`[${label}](${url})`);
      }
    } else if (match[4] !== undefined) {
      nodes.push(<em key={key}>{match[4]}</em>);
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes.length > 0 ? nodes : [text];
};

const BULLET_RE = /^\s*[-*]\s+(.*)$/;
const NUMBERED_RE = /^\s*\d+[.)]\s+(.*)$/;
// SAM-1009: "## Program" is a heading (an ATX heading: 1-3 hashes, a space, the text).
const HEADING_RE = /^\s{0,3}(#{1,3})\s+(.+?)\s*#*\s*$/;
const HEADING_SIZE: Record<number, string> = { 1: '20px', 2: '17px', 3: '15px' };

export const renderMarkdownLite = (text: string): React.ReactNode => {
  if (!text) return null;
  const lines = text.replace(/\r\n/g, '\n').split('\n');

  const blocks: React.ReactNode[] = [];
  let i = 0;
  let blockKey = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i++;
      continue;
    }

    const heading = line.match(HEADING_RE);
    if (heading) {
      const level = heading[1].length;
      const Tag = (level === 1 ? 'h3' : level === 2 ? 'h4' : 'h5') as 'h3' | 'h4' | 'h5';
      blocks.push(
        <Tag key={`h-${blockKey++}`} style={{ margin: '4px 0 8px 0', fontSize: HEADING_SIZE[level], fontWeight: 700, lineHeight: 1.3 }}>
          {renderInline(heading[2], `h${blockKey}`)}
        </Tag>,
      );
      i++;
      continue;
    }

    if (BULLET_RE.test(line)) {
      const items: string[] = [];
      while (i < lines.length && BULLET_RE.test(lines[i])) {
        items.push(lines[i].match(BULLET_RE)![1]);
        i++;
      }
      blocks.push(
        <ul key={`b-${blockKey++}`} style={{ margin: '0 0 12px 0', paddingLeft: '20px' }}>
          {items.map((item, idx) => (
            <li key={idx}>{renderInline(item, `b${blockKey}-${idx}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    if (NUMBERED_RE.test(line)) {
      const items: string[] = [];
      while (i < lines.length && NUMBERED_RE.test(lines[i])) {
        items.push(lines[i].match(NUMBERED_RE)![1]);
        i++;
      }
      blocks.push(
        <ol key={`n-${blockKey++}`} style={{ margin: '0 0 12px 0', paddingLeft: '20px' }}>
          {items.map((item, idx) => (
            <li key={idx}>{renderInline(item, `n${blockKey}-${idx}`)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    // Paragraph: consecutive non-blank, non-list lines. Single newlines
    // inside it become <br/> so manually-typed line breaks (the common case
    // for pre-existing plain-text descriptions) are preserved too.
    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !BULLET_RE.test(lines[i]) && !NUMBERED_RE.test(lines[i]) && !HEADING_RE.test(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    const paraNodes: React.ReactNode[] = [];
    paraLines.forEach((pLine, idx) => {
      if (idx > 0) paraNodes.push(<br key={`br-${blockKey}-${idx}`} />);
      paraNodes.push(...renderInline(pLine, `p${blockKey}-${idx}`));
    });
    blocks.push(
      <p key={`p-${blockKey++}`} style={{ margin: '0 0 12px 0' }}>
        {paraNodes}
      </p>,
    );
  }

  return blocks;
};
